// Geometry for box-based shapes (rectangle + ellipse). Both are stored as four
// corner points in image space (TL, TR, BR, BL) forming a possibly-rotated
// rectangle — rotation is baked into the corners, so no schema change is needed.
// The ellipse is the one inscribed in that box.

import type { AnnPoint } from './types';

/** Center / rotation / half-extents of a 4-corner (TL,TR,BR,BL) rectangle. */
export function rectFrame(corners: AnnPoint[]) {
  const c0 = corners[0];
  const c1 = corners[1];
  const c2 = corners[2];
  const c3 = corners[3];
  const center = { x: (c0.x + c2.x) / 2, y: (c0.y + c2.y) / 2 };
  const w = Math.hypot(c1.x - c0.x, c1.y - c0.y);
  const h = Math.hypot(c3.x - c0.x, c3.y - c0.y);
  const angle = Math.atan2(c1.y - c0.y, c1.x - c0.x);
  return { center, angle, hx: w / 2, hy: h / 2, w, h };
}

/** The 4 corners (TL,TR,BR,BL) of a rectangle at center/angle with half-extents. */
export function cornersFrom(center: AnnPoint, angle: number, hx: number, hy: number): AnnPoint[] {
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  const vx = -Math.sin(angle);
  const vy = Math.cos(angle);
  const mk = (sx: number, sy: number): AnnPoint => ({
    x: center.x + sx * hx * ux + sy * hy * vx,
    y: center.y + sx * hx * uy + sy * hy * vy,
  });
  return [mk(-1, -1), mk(1, -1), mk(1, 1), mk(-1, 1)];
}

/** Whether two vertex lists are the same points in the same order. */
export function pointsEqual(a: AnnPoint[], b: AnnPoint[]): boolean {
  return a.length === b.length && a.every((p, i) => p.x === b[i].x && p.y === b[i].y);
}

/** Axis-aligned rectangle corners from two opposite points (draw creation). */
export function bboxCorners(a: AnnPoint, b: AnnPoint): AnnPoint[] {
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxY = Math.max(a.y, b.y);
  return [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY },
  ];
}

const SIGNS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
];

/** Recompute corners when dragging corner `i` to image point D, keeping the
 *  opposite corner fixed and the box rectangular at its current angle. */
export function resizeCorner(corners: AnnPoint[], i: number, D: AnnPoint): AnnPoint[] {
  const { center, angle, hx, hy } = rectFrame(corners);
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  const vx = -Math.sin(angle);
  const vy = Math.cos(angle);
  const [sxi, syi] = SIGNS[i];
  const oLx = -sxi * hx; // fixed opposite corner, in the box's local frame
  const oLy = -syi * hy;
  const dx = D.x - center.x;
  const dy = D.y - center.y;
  const Lx = dx * ux + dy * uy; // pointer in local frame
  const Ly = dx * vx + dy * vy;
  const nHx = Math.max(1, Math.abs(Lx - oLx) / 2);
  const nHy = Math.max(1, Math.abs(Ly - oLy) / 2);
  const cLx = (Lx + oLx) / 2;
  const cLy = (Ly + oLy) / 2;
  const nc = { x: center.x + cLx * ux + cLy * vx, y: center.y + cLx * uy + cLy * vy };
  return cornersFrom(nc, angle, nHx, nHy);
}

/** Signed-doubled area of a polygon (shoelace); positive is CCW in image space. */
function shoelace(pts: AnnPoint[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a;
}

/** Absolute polygon area in image-pixel² (shoelace). */
export function polygonArea(pts: AnnPoint[]): number {
  return Math.abs(shoelace(pts)) / 2;
}

/** Area-weighted polygon centroid, falling back to the vertex mean when the
 *  polygon is degenerate (near-zero area). */
export function polygonCentroid(pts: AnnPoint[]): AnnPoint {
  const a = shoelace(pts);
  if (Math.abs(a) < 1e-9) {
    const n = pts.length || 1;
    return {
      x: pts.reduce((s, p) => s + p.x, 0) / n,
      y: pts.reduce((s, p) => s + p.y, 0) / n,
    };
  }
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = pts[(i + 1) % pts.length];
    const cross = p.x * q.y - q.x * p.y;
    cx += (p.x + q.x) * cross;
    cy += (p.y + q.y) * cross;
  }
  return { x: cx / (3 * a), y: cy / (3 * a) };
}

/** Axis-aligned bounds of a point set (image space). */
export function polygonBounds(pts: AnnPoint[]) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

/** Sample a closed centripetal Catmull-Rom spline through `pts` into image-space
 *  points. Centripetal (alpha=0.5) avoids the cusps/self-intersections plain
 *  Catmull-Rom produces on sharp turns, so the sampled outline stays a valid
 *  simple loop — its shoelace area is meaningful. `perSeg` points per edge. */
export function smoothClosedPoints(pts: AnnPoint[], perSeg = 12, alpha = 0.5): AnnPoint[] {
  const n = pts.length;
  if (n < 3) return pts.slice();
  const knot = (ti: number, a: AnnPoint, b: AnnPoint) =>
    ti + Math.pow(Math.max(Math.hypot(b.x - a.x, b.y - a.y), 1e-6), alpha);
  const out: AnnPoint[] = [];
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const t0 = 0;
    const t1 = knot(t0, p0, p1);
    const t2 = knot(t1, p1, p2);
    const t3 = knot(t2, p2, p3);
    for (let k = 0; k < perSeg; k++) {
      const t = t1 + ((t2 - t1) * k) / perSeg;
      const lerp = (u: number, va: number, vb: number, ta: number, tb: number) =>
        ((tb - u) / (tb - ta)) * va + ((u - ta) / (tb - ta)) * vb;
      const a1x = lerp(t, p0.x, p1.x, t0, t1);
      const a1y = lerp(t, p0.y, p1.y, t0, t1);
      const a2x = lerp(t, p1.x, p2.x, t1, t2);
      const a2y = lerp(t, p1.y, p2.y, t1, t2);
      const a3x = lerp(t, p2.x, p3.x, t2, t3);
      const a3y = lerp(t, p2.y, p3.y, t2, t3);
      const b1x = lerp(t, a1x, a2x, t0, t2);
      const b1y = lerp(t, a1y, a2y, t0, t2);
      const b2x = lerp(t, a2x, a3x, t1, t3);
      const b2y = lerp(t, a2y, a3y, t1, t3);
      out.push({ x: lerp(t, b1x, b2x, t1, t2), y: lerp(t, b1y, b2y, t1, t2) });
    }
  }
  return out;
}

/** Rotate points about `c` by `delta` radians (used to spin a polygon). */
export function rotatePointsAround(pts: AnnPoint[], c: AnnPoint, delta: number): AnnPoint[] {
  const cos = Math.cos(delta);
  const sin = Math.sin(delta);
  return pts.map((p) => {
    const dx = p.x - c.x;
    const dy = p.y - c.y;
    return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos };
  });
}

/** Sample the inscribed ellipse of the corner-box as image-space points. */
export function ellipsePoints(corners: AnnPoint[], n = 48): AnnPoint[] {
  const { center, angle, hx, hy } = rectFrame(corners);
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  const vx = -Math.sin(angle);
  const vy = Math.cos(angle);
  const out: AnnPoint[] = [];
  for (let k = 0; k < n; k++) {
    const t = (2 * Math.PI * k) / n;
    const cx = Math.cos(t) * hx;
    const cy = Math.sin(t) * hy;
    out.push({ x: center.x + cx * ux + cy * vx, y: center.y + cx * uy + cy * vy });
  }
  return out;
}

/**
 * Where a region's label (its name and area) sits on screen, y down: centred
 * over the outline and `gap` px clear of its highest point, so the label
 * covers nothing the region encloses. `halfHeight` is half the label's height.
 * A label that would cross the top of the view (y = 0) goes `gap` px under the
 * lowest point instead, still outside. `outline` needs at least one point.
 */
export function labelOutside(outline: AnnPoint[], gap: number, halfHeight: number): AnnPoint {
  const b = polygonBounds(outline);
  const x = (b.minX + b.maxX) / 2;
  const above = b.minY - gap - halfHeight;
  return above - halfHeight >= 0 ? { x, y: above } : { x, y: b.maxY + gap + halfHeight };
}
